// ============================================================================
// BP-13-02: Admin Resources Hooks
// BrightPath Platform - Admin Frontend
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminResourcesApi } from '@/features/admin/api/adminApi';
import type {
  ResourceListParams,
  CreateResource,
  UpdateResource,
} from '@/types/admin';
import { useToast } from '@/core/hooks/useToast';

// =============================================================================
// Query Keys
// =============================================================================

export const resourceKeys = {
  all: ['admin', 'resources'] as const,
  lists: () => [...resourceKeys.all, 'list'] as const,
  list: (params: ResourceListParams) => [...resourceKeys.lists(), params] as const,
  details: () => [...resourceKeys.all, 'detail'] as const,
  detail: (id: string) => [...resourceKeys.details(), id] as const,
  categories: () => [...resourceKeys.all, 'categories'] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch paginated list of resources
 */
export function useAdminResources(params: ResourceListParams) {
  return useQuery({
    queryKey: resourceKeys.list(params),
    queryFn: () => adminResourcesApi.list(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetch a single resource
 */
export function useAdminResource(resourceId: string) {
  return useQuery({
    queryKey: resourceKeys.detail(resourceId),
    queryFn: () => adminResourcesApi.get(resourceId),
    enabled: !!resourceId,
  });
}

/**
 * Fetch resource categories
 */
export function useResourceCategories() {
  return useQuery({
    queryKey: resourceKeys.categories(),
    queryFn: () => adminResourcesApi.getCategories(),
    staleTime: 5 * 60 * 1000, // 5 minutes - categories don't change often
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Create a new resource
 */
export function useCreateResource() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateResource) => adminResourcesApi.create(data),
    onSuccess: (resource) => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
      queryClient.setQueryData(resourceKeys.detail(resource.id), resource);
      toast({
        title: 'Resource created',
        description: `"${resource.title}" has been created successfully.`,
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Creation failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Update a resource
 */
export function useUpdateResource() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ resourceId, data }: { resourceId: string; data: UpdateResource }) =>
      adminResourcesApi.update(resourceId, data),
    onSuccess: (resource) => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
      queryClient.setQueryData(resourceKeys.detail(resource.id), resource);
      toast({
        title: 'Resource updated',
        description: `"${resource.title}" has been updated successfully.`,
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Delete a resource
 */
export function useDeleteResource() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (resourceId: string) => adminResourcesApi.delete(resourceId),
    onSuccess: (_, resourceId) => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
      queryClient.removeQueries({ queryKey: resourceKeys.detail(resourceId) });
      toast({
        title: 'Resource deleted',
        description: 'The resource has been deleted successfully.',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Delete failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Publish a resource
 */
export function usePublishResource() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (resourceId: string) => adminResourcesApi.publish(resourceId),
    onSuccess: (resource) => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
      queryClient.setQueryData(resourceKeys.detail(resource.id), resource);
      toast({
        title: 'Resource published',
        description: `"${resource.title}" is now live.`,
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Publish failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Unpublish a resource
 */
export function useUnpublishResource() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (resourceId: string) => adminResourcesApi.unpublish(resourceId),
    onSuccess: (resource) => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
      queryClient.setQueryData(resourceKeys.detail(resource.id), resource);
      toast({
        title: 'Resource unpublished',
        description: `"${resource.title}" has been unpublished.`,
        variant: 'info',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Unpublish failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Upload featured image
 */
export function useUploadResourceImage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ resourceId, file }: { resourceId: string; file: File }) =>
      adminResourcesApi.uploadImage(resourceId, file),
    onSuccess: (_, { resourceId }) => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.detail(resourceId) });
      toast({
        title: 'Image uploaded',
        description: 'Featured image has been updated.',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}


